import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, AlertTriangle, AlertCircle, Lightbulb, Loader2, Send,
} from 'lucide-react';

import { Card, Button, Input, Select, Textarea, AreaCard, Skeleton, ToggleGroup, ToggleGroupItem } from '../ui-v2';
import { cn } from '../../../lib/cn';
import { getIcon } from '../inventario/icons';
import { UsuarioContext } from '../../providers/personaContext';
import { listAreasActivas, crearTicket } from './api';
import TechInfoBox from './components/TechInfoBox';
import UploadImagenes from './components/UploadImagenes';
import PersonaPicker from './components/PersonaPicker';

const MODULOS_INTRANET = [
  'Vacaciones / Ausencias', 'Inventario', 'Formación LAE', 'Formación SEPE',
  'Gestión de personas', 'Intervenciones técnicas', 'Blog / Comunicación',
  'Organigrama', 'Estadísticas RingOver', 'Login / Acceso', 'Otro / General',
];
const DISPOSITIVOS = [
  'Ordenador portátil', 'Ordenador sobremesa', 'Monitor', 'Teclado / Ratón',
  'Móvil corporativo', 'Impresora', 'Auriculares / Webcam', 'Otro',
];
// Prioridad (solo técnico, 3.5.G). "Sin prioridad" (null) va primero. El punto de
// color sustituye al emoji del mockup (🟢🟡🔴🚨).
const PRIORIDADES = [
  { v: null, label: 'Sin prioridad', dot: 'bg-slate-300' },
  { v: 'baja', label: 'Baja', dot: 'bg-green-500' },
  { v: 'media', label: 'Media', dot: 'bg-amber-500' },
  { v: 'alta', label: 'Alta', dot: 'bg-orange-500' },
  { v: 'critica', label: 'Crítica', dot: 'bg-red-600' },
];

// Etiqueta de sección (.sec-lbl del mockup) y de campo (.flabel del mockup).
const SEC_LBL = 'text-[11px] font-semibold uppercase tracking-[0.6px] text-slate-400 mb-2';
const FLABEL = 'block text-[11px] font-semibold uppercase tracking-[0.5px] text-slate-600 mb-1.5';
const OPCIONAL = <span className="font-normal normal-case tracking-normal text-slate-400">(opcional)</span>;

export default function SoporteNuevo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preseleccionado = useRef(false);

  // Rol: solo los técnicos (soporte.panel.manage) o Super Admin pueden crear en
  // nombre de otros y fijar prioridad. Los usuarios normales no ven estas piezas.
  const { persona, permisos } = useContext(UsuarioContext);
  const puedeCrearEnNombre = (persona?.rol?.toLowerCase() === 'super administrador')
    || (permisos || []).includes('soporte.panel.manage');

  const [areas, setAreas] = useState([]);
  const [cargandoAreas, setCargandoAreas] = useState(true);

  const [tipo, setTipo] = useState('incidencia');
  const [categoriaId, setCategoriaId] = useState(null);
  const [moduloIntranet, setModuloIntranet] = useState('');
  const [tipoDispositivo, setTipoDispositivo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');

  // 3.5.G — "en nombre de" + prioridad (solo técnico).
  const [paraMi, setParaMi] = useState(true);
  const [enNombreDe, setEnNombreDe] = useState(null); // {id,nombre,email,tipo,empresa}|null
  const [prioridad, setPrioridad] = useState(null);   // null='Sin prioridad'
  const [errorPersona, setErrorPersona] = useState(null);
  const [errorPrioridad, setErrorPrioridad] = useState(null);

  const [infoEnabled, setInfoEnabled] = useState(true);
  const [infoTecnica, setInfoTecnica] = useState(null);
  const [imagenes, setImagenes] = useState([]);

  const [enviando, setEnviando] = useState(false);
  const [errores, setErrores] = useState({});       // { titulo, descripcion }
  const [errorGeneral, setErrorGeneral] = useState(null);

  const cargarAreas = () => {
    setCargandoAreas(true);
    listAreasActivas()
      .then(setAreas)
      .catch(() => setErrorGeneral('No se pudieron cargar las áreas.'))
      .finally(() => setCargandoAreas(false));
  };
  useEffect(() => { cargarAreas(); }, []);

  // Precarga desde el botón "Reportar problema" del topbar (una sola vez, tras cargar áreas):
  // ?categoria=intranet → preselecciona el área Intranet; ?modulo=... → preselecciona módulo.
  useEffect(() => {
    if (preseleccionado.current || areas.length === 0) return;
    const catParam = searchParams.get('categoria');
    const modParam = searchParams.get('modulo');
    if (catParam === 'intranet') {
      const intranet = areas.find((a) => a.slug === 'intranet');
      if (intranet) {
        setCategoriaId(intranet.id);
        if (modParam) setModuloIntranet(modParam);
      }
    }
    preseleccionado.current = true;
  }, [areas, searchParams]);

  const areaSel = areas.find((a) => a.id === categoriaId) || null;
  const esIntranet = areaSel?.slug === 'intranet';
  const esHardware = areaSel?.slug === 'hardware';

  const valido =
    !!categoriaId &&
    titulo.trim().length >= 5 &&
    descripcion.trim().length >= 10;

  async function enviar() {
    setErrores({});
    setErrorGeneral(null);
    setErrorPersona(null);
    setErrorPrioridad(null);

    // 3.5.G — si el técnico crea "para otra persona", debe haber elegido a alguien.
    if (puedeCrearEnNombre && !paraMi && !enNombreDe) {
      setErrorPersona('Selecciona una persona.');
      return;
    }

    setEnviando(true);

    // Multipart: campos + imágenes + info técnica (axios fija el Content-Type).
    const fd = new FormData();
    fd.append('tipo', tipo);
    fd.append('categoria_id', categoriaId);
    fd.append('titulo', titulo.trim());
    fd.append('descripcion', descripcion.trim());
    // 3.5.G — crear en nombre de otro (solo técnico + "Para otra persona").
    if (puedeCrearEnNombre && !paraMi && enNombreDe) {
      fd.append('en_nombre_de[persona_id]', enNombreDe.id);
      fd.append('en_nombre_de[persona_tipo]', enNombreDe.tipo);
    }
    // Prioridad: solo "Para otra persona" y solo si eligió una (null = "Sin
    // prioridad" → no se envía). En "Para mí" nunca se envía (política 3.5.F).
    if (puedeCrearEnNombre && !paraMi && prioridad !== null) {
      fd.append('prioridad', prioridad);
    }
    if (esIntranet && moduloIntranet) fd.append('modulo_intranet', moduloIntranet);
    if (esHardware && tipoDispositivo) fd.append('tipo_dispositivo', tipoDispositivo);
    if (infoTecnica) {
      // Si venimos del botón "Reportar problema", la URL del módulo origen es más
      // útil que la del propio formulario (que captura detectarTech).
      const urlOrigen = searchParams.get('url');
      const info = urlOrigen ? { ...infoTecnica, url: urlOrigen } : infoTecnica;
      fd.append('info_tecnica', JSON.stringify(info));
    }
    imagenes.forEach((img) => fd.append('imagenes', img));

    try {
      const ticket = await crearTicket(fd);
      toast.success(`Ticket ${ticket.numero} creado correctamente`);
      navigate(`/soporte/ticket/${ticket.id}`);
    } catch (e) {
      const data = e?.response?.data;
      const code = data?.error;
      if (code === 'titulo_invalido') setErrores({ titulo: data.message });
      else if (code === 'descripcion_invalida') setErrores({ descripcion: data.message });
      else if (code === 'mime_no_permitido' || code === 'archivo_grande' || code === 'limite_adjuntos') {
        toast.error(data.message);
      } else if (code === 'categoria_inactiva') {
        setErrorGeneral('Esta área ya no admite tickets, elige otra.');
        setCategoriaId(null);
        cargarAreas();
      } else if (code === 'sin_permiso_crear_en_nombre_de') {
        toast.error('No tienes permiso para crear tickets en nombre de otros.');
      } else if (code === 'en_nombre_de_invalido' || code === 'persona_no_encontrada') {
        setErrorPersona(data.message || 'Persona no válida.');
      } else if (code === 'prioridad_no_aplicable') {
        setErrorPrioridad(data.message || 'Las sugerencias no tienen prioridad.');
      } else if (code === 'prioridad_invalida') {
        toast.error(data.message || 'Prioridad no válida.');
      } else if (code === 'tipo_invalido' || code === 'categoria_requerida' || code === 'categoria_no_encontrada') {
        setErrorGeneral(data.message);
      } else {
        toast.error('No se pudo crear el ticket.');
      }
      setEnviando(false);
    }
  }

  return (
    <div className="px-6 py-6 min-h-full">
      {/* Cabecera */}
      <button
        type="button"
        onClick={() => navigate('/soporte/mis-tickets')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-3"
      >
        <ArrowLeft className="w-4 h-4" /> Mis tickets
      </button>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900">Nuevo ticket</h1>
        <p className="text-sm text-slate-500 mt-1">Cuéntanos qué pasa y lo gestionaremos cuanto antes.</p>
      </div>

      {errorGeneral && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {errorGeneral}
        </div>
      )}

      <Card className="p-5 border-slate-200">
        {/* ¿Para quién? — solo técnicos (crear en nombre de otro). 3.5.G */}
        {puedeCrearEnNombre && (
          <div className="mb-5 pb-5 border-b border-slate-100">
            <div className={SEC_LBL}>¿Para quién es este ticket?</div>
            <ToggleGroup>
              <ToggleGroupItem active={paraMi} onClick={() => { setParaMi(true); setErrorPersona(null); }}>Para mí</ToggleGroupItem>
              <ToggleGroupItem active={!paraMi} onClick={() => setParaMi(false)}>Para otra persona</ToggleGroupItem>
            </ToggleGroup>
            {!paraMi && (
              <div className="mt-3 max-w-md">
                <label className={FLABEL}>Persona en cuyo nombre creas el ticket</label>
                <PersonaPicker
                  value={enNombreDe}
                  onChange={(p) => { setEnNombreDe(p); setErrorPersona(null); }}
                  disabled={enviando}
                  error={errorPersona}
                />
                <div className="text-[11px] text-slate-400 mt-1.5">
                  El ticket quedará a nombre de esta persona. Ella recibirá el email de confirmación.
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 [@media(min-width:1100px)]:grid-cols-2 gap-6">

          {/* ───────── COLUMNA IZQUIERDA: qué / cómo ───────── */}
          <div className="flex flex-col gap-3.5">

            {/* Tipo de ticket */}
            <div>
              <div className={SEC_LBL}>Tipo de ticket</div>
              <div className="grid grid-cols-2 gap-2.5">
                <TipoCard
                  activo={tipo === 'incidencia'} onClick={() => setTipo('incidencia')}
                  icon={<AlertTriangle className="w-[26px] h-[26px]" strokeWidth={1.6} />}
                  titulo="Incidencia" desc="Algo no funciona" colorClass="text-red-600"
                />
                <TipoCard
                  activo={tipo === 'sugerencia'} onClick={() => { setTipo('sugerencia'); setPrioridad(null); setErrorPrioridad(null); }}
                  icon={<Lightbulb className="w-[26px] h-[26px]" strokeWidth={1.6} />}
                  titulo="Sugerencia" desc="Propón una mejora" colorClass="text-primary"
                />
              </div>
            </div>

            {/* Título */}
            <div>
              <label className={FLABEL}>Título descriptivo</label>
              <Input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                maxLength={200}
                placeholder="Ej: No puedo acceder al módulo de vacaciones"
              />
              <div className="flex justify-between mt-1">
                {errores.titulo
                  ? <span className="text-xs text-red-600 flex items-start gap-1"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{errores.titulo}</span>
                  : <span className="text-[11px] text-slate-400">Mínimo 5 caracteres</span>}
                <span className="text-[11px] text-slate-400">{titulo.length}/200</span>
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label className={FLABEL}>Descripción detallada</label>
              <Textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows={4}
                placeholder="Describe el problema: qué ibas a hacer, qué ocurrió, si viste algún mensaje de error…"
              />
              <div className="flex justify-between mt-1">
                {errores.descripcion
                  ? <span className="text-xs text-red-600 flex items-start gap-1"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{errores.descripcion}</span>
                  : <span className="text-[11px] text-slate-400">Mínimo 10 caracteres</span>}
                <span className="text-[11px] text-slate-400">{descripcion.length}</span>
              </div>
            </div>

            {/* Prioridad — solo técnico, solo "Para otra persona", solo incidencias
                (3.5.G). El backend solo acepta prioridad cuando hay en_nombre_de;
                para el propio técnico rige 3.5.F (sin prioridad al crear). */}
            {puedeCrearEnNombre && !paraMi && tipo === 'incidencia' && (
              <div>
                <label className={FLABEL}>Prioridad {OPCIONAL}</label>
                <div className="flex flex-wrap gap-1.5">
                  {PRIORIDADES.map((p) => (
                    <PrioPill
                      key={p.label}
                      activo={prioridad === p.v}
                      onClick={() => { setPrioridad(p.v); setErrorPrioridad(null); }}
                      dotClass={p.dot}
                      label={p.label}
                    />
                  ))}
                </div>
                {errorPrioridad && (
                  <p className="text-xs text-red-600 mt-1 flex items-start gap-1"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{errorPrioridad}</p>
                )}
              </div>
            )}
          </div>

          {/* ───────── COLUMNA DERECHA: dónde / extras ───────── */}
          <div className="flex flex-col gap-3.5">

            {/* Área */}
            <div>
              <div className={SEC_LBL}>Área</div>
              {cargandoAreas ? (
                <div className="grid grid-cols-4 gap-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-border py-2.5 px-1.5">
                      <Skeleton width={32} height={32} className="rounded-md" />
                      <Skeleton className="h-3 w-12" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {areas.map((a) => {
                    const Icono = getIcon(a.icono);
                    return (
                      <AreaCard
                        key={a.id}
                        icon={<Icono size={16} />}
                        label={a.nombre}
                        area={a.color || 'otros'}
                        selected={categoriaId === a.id}
                        onClick={() => setCategoriaId(a.id)}
                        className="w-full h-auto py-2.5 px-1.5"
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Campo condicional: módulo intranet */}
            {esIntranet && (
              <div>
                <label className={FLABEL}>Módulo de la intranet afectado {OPCIONAL}</label>
                <Select value={moduloIntranet} onChange={(e) => setModuloIntranet(e.target.value)}>
                  <option value="">Seleccionar módulo…</option>
                  {MODULOS_INTRANET.map((m) => <option key={m} value={m}>{m}</option>)}
                </Select>
              </div>
            )}

            {/* Campo condicional: tipo de dispositivo */}
            {esHardware && (
              <div>
                <label className={FLABEL}>Tipo de dispositivo {OPCIONAL}</label>
                <Select value={tipoDispositivo} onChange={(e) => setTipoDispositivo(e.target.value)}>
                  <option value="">Seleccionar dispositivo…</option>
                  {DISPOSITIVOS.map((d) => <option key={d} value={d}>{d}</option>)}
                </Select>
              </div>
            )}

            {/* Información técnica */}
            <div>
              <label className={FLABEL}>Información técnica</label>
              <TechInfoBox
                value={infoTecnica}
                onChange={setInfoTecnica}
                enabled={infoEnabled}
                onToggleEnabled={setInfoEnabled}
              />
            </div>

            {/* Capturas / imágenes */}
            <div>
              <label className={FLABEL}>Capturas o imágenes {OPCIONAL}</label>
              <UploadImagenes value={imagenes} onChange={setImagenes} disabled={enviando} />
            </div>
          </div>

        </div>

        {/* Acciones */}
        <div className="flex items-center justify-end gap-3 mt-4 pt-3.5 border-t border-slate-100">
          <Button variant="outline" onClick={() => navigate('/soporte/mis-tickets')} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!valido || enviando}>
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {enviando ? 'Enviando…' : 'Enviar ticket'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// Tarjeta de tipo (Incidencia / Sugerencia) — inline; si emerge en otra pantalla se promueve a ui-v2.
function TipoCard({ activo, onClick, icon, titulo, desc, colorClass }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-center rounded-[10px] border-[1.5px] px-3 py-3.5 transition-all',
        activo ? 'border-primary bg-blue-50' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
      )}
    >
      <div className={cn('flex justify-center mb-1.5', colorClass)}>{icon}</div>
      <div className="text-[13px] font-semibold text-slate-900">{titulo}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{desc}</div>
    </button>
  );
}

// Pill de prioridad (segmented) — inline. El punto de color sustituye al emoji del mockup.
function PrioPill({ activo, onClick, dotClass, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border px-3 py-1.5 text-xs font-medium transition-colors',
        activo ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
      )}
    >
      <span className={cn('w-2 h-2 rounded-full shrink-0', dotClass)} />
      {label}
    </button>
  );
}
